import React, { useEffect, useRef, useState } from 'react'
import { Button, Badge, Table, ButtonGroup } from 'react-bootstrap'
import * as s from './EnterPicksGameItem.scss'
import FontAwesomeIcon from '@severed-links/common.font-awesome-icon'

import EnterPicksGameMatchupTable from './EnterPicksGameMatchupTable'
import EnterPicksRecentGame from './EnterPicksRecentGame'
import FavoriteUnderdogButton from './FavoriteUnderdogButton'
import LineContainer from './LineContainer'
import RankButtonsContainer from './RankButtonsContainer'
import { getEnterPicksNFLMatchUp } from '@severed-links/common.redherrings-reducers/football'
import { useDrag } from 'react-dnd'
import { DRAG_AND_DROP_ITEM_TYPES } from '@severed-links/common.redherrings-constants'
import { useDispatch } from 'react-redux'
import { useWindowWidth } from '@react-hook/window-size'

const EnterPicksGameItem = ({ game = {}, pickGameData, seasonName, addCommand, ranks = [], disabled = false }) => {

    const dispatch = useDispatch()
    const [showGameData, setShowGameData] = useState(false)
    const [showRankOptions, setShowRankOptions] = useState(false)
    const [gameDataError, setGameDataError] = useState('')
    const [gameDataLoading, setGameDataLoading] = useState(false)
    const rankHandle = useRef(null)
    const width = useWindowWidth()
    useEffect(() => { if (disabled) setShowRankOptions(false) }, [disabled])

    const [{ isDragging }, _dragRef, _previewRef] = useDrag(() => ({
        type: DRAG_AND_DROP_ITEM_TYPES.REGULAR_SEASON_GAME,
        canDrag: !disabled,
        collect: (monitor, props) => ({ isDragging: monitor.isDragging() }),
        item: () => ({ game: game.game }),
        end: (item, monitor) => {
            const dropResult = monitor.getDropResult()
            if (!disabled && dropResult && addCommand) {
                addCommand({ type: 'changeRank', ...dropResult, game: game.game, oldRank: game.rank })
            }    
        }
    }), [game.game, game.rank, disabled, addCommand])

    const handleClick = (game, pick) => { if (!disabled) addCommand({ type: 'makePick', game, pick }) }

    const getGameData = async () => {
        setShowGameData(true)
        if (pickGameData || gameDataLoading) return
        setGameDataError('')
        setGameDataLoading(true)
        try {
            const result = await dispatch(getEnterPicksNFLMatchUp(seasonName, game.week, game.game))
            if (result?.error) throw new Error('Game details could not be loaded. Please try again.')
        } catch (error) {
            setGameDataError('Game details could not be loaded. Please try again.')
        }
          setGameDataLoading(false)
    }

    const hideGameData = () => setShowGameData(false)

    const toggleMiniRankMenu = (e, _showRankOptions) => {
        setShowRankOptions(_showRankOptions)
        if (!_showRankOptions) rankHandle.current?.focus()
        e.stopPropagation()
    }

    const handleMiniRankClick = (e, newRank) => {
        toggleMiniRankMenu(e, false)
        if (disabled) return
        addCommand({ type: 'changeRank', ...{ newRank }, game: game.game, oldRank: game.rank })
    }

    const isMini = width <= 700
    return (
        <div ref={_previewRef} className={s.container + (isDragging ? ' ' + s.isDragging : '') + (showGameData ? ' ' + s.showPickGameDataContainer : '')}>

            <button type='button' disabled={disabled} aria-label={`Change points for ${game.favoriteMascot} versus ${game.underdogMascot}`}
                aria-expanded={showRankOptions && !disabled} title='Drag to reorder or click to choose points'
                ref={node => { rankHandle.current = node; _dragRef(node) }} className={s.sortHandleContainer} onClick={e => toggleMiniRankMenu(e, !showRankOptions)}>
                <FontAwesomeIcon name='grip-vertical' />
            </button>

            <RankButtonsContainer ranks={ranks} 
                game={game}
                show={showRankOptions && !disabled}
                onClick={handleMiniRankClick} 
                onClose={toggleMiniRankMenu} />
            <FavoriteUnderdogButton game={game} pick='Favorite' disabled={disabled} isMini={isMini} onClick={handleClick} />
            <LineContainer game={game} isMini={isMini} />
            <FavoriteUnderdogButton game={game} pick='Underdog' disabled={disabled} isMini={isMini} onClick={handleClick} />
            <button type='button' aria-label={showGameData ? 'Hide game details' : 'Show game details'} aria-expanded={showGameData} className={s.gameInfoContainer} onClick={showGameData ? () => hideGameData() : () => getGameData()}>
                <FontAwesomeIcon name={showGameData ? 'circle-chevron-up' : 'circle-info'} />
            </button>
            {showGameData && gameDataLoading ? <div className={s.pickGameDataContainer} role='status'>Loading game details…</div> : null}
            {showGameData && gameDataError ? <div className={s.pickGameDataContainer} role='alert'>{gameDataError} <Button variant='link' onClick={getGameData}>Try again</Button></div> : null}
            {pickGameData && showGameData ?
            <div className={s.pickGameDataContainer}>
                <div className={s.detailsHeading}><h3>Game comparison</h3><Button variant='link' onClick={hideGameData}>Close details</Button></div>
                <EnterPicksGameMatchupTable matchUp={pickGameData.matchUp} homeFavorite={game.homeFavorite}
                    favoriteName={game.favoriteMascot} underdogName={game.underdogMascot} />
                <h4 className={s.historyHeading}>Recent meetings</h4>
                {!pickGameData.recentGames?.length ? <p>No recent meetings available.</p> : null}
                <div>
                    <div>
                        <div className={s.recentGameContainer}>
                        {pickGameData.recentGames && pickGameData.recentGames.map((r, index) => 
                            <EnterPicksRecentGame game={r} index={index} key={`recent-game-item-${index}`} />
                        )}
                        </div>
                    </div>
                </div>
            </div>
            : null}
        </div>
    )
}
    
export default EnterPicksGameItem
    